import React, { useState, useEffect } from "react";
import CssBaseline from "@mui/material/CssBaseline";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import Button from "@mui/material/Button";
import Link from "@mui/material/Link";
import { makeStyles } from '@mui/styles';
import axios from "../utils/axiosConfig";
import { fetchMe } from "../utils/authService";
import { WEB_URL } from "../utils/constants";
import ahGreenDotLogo from "../images/assesshub_logo_green_dot.svg";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';

function Copyright() {
  return (
    <Typography variant="body2" color="textSecondary" align="center">
      {"Copyright © "}
      <Link color="inherit" href={WEB_URL}>
        AssessHub
      </Link>{" "}
      {new Date().getFullYear()}
      {"."}
    </Typography>
  );
}

const useStyles = makeStyles((theme) => ({
  root: {
    height: "100vh",
  },
  image: {
    backgroundImage: "url('./../../v2/images/login-page.svg')",
    backgroundRepeat: "no-repeat",
    backgroundColor: theme?.palette?.mode === "dark" ? theme?.palette?.grey?.[900] : theme?.palette?.grey?.[50] || '#fafafa',
    backgroundSize: "cover",
    backgroundPosition: "center",
  },
  paper: {
    padding: theme?.spacing?.(8, 4) || '64px 32px',
    display: "table-cell",
    verticalAlign: "middle",
    alignItems: "center",
    textAlign: "center",
  },
  statusIcon: {
    fontSize: "64px",
    marginBottom: theme?.spacing?.(2) || '16px',
  },
  successIcon: {
    color: "#4caf50",
  },
  errorIcon: {
    color: "#f44336",
  },
}));

export default function MagicLogin(props) {
  const classes = useStyles();
  const [status, setStatus] = useState("verifying"); // verifying, success, error
  const [message, setMessage] = useState("Verifying your magic link...");
  const [errorDetails, setErrorDetails] = useState("");

  useEffect(() => {
    const verifyMagicLink = async () => {
      try {
        // Get token from URL
        const urlParams = new URLSearchParams(window.location.search);
        const token = urlParams.get("token");

        if (!token) {
          setStatus("error");
          setMessage("Invalid magic link");
          setErrorDetails("No token provided in the URL. Please use the complete magic link.");
          return;
        }

        console.log("[MAGIC-LOGIN] Verifying token...");

        // Call the backend to verify the token
        const response = await axios.post("/magic-link/verify", {
          token: token
        });

        console.log("[MAGIC-LOGIN] Response:", response.data);

        if (response.data.status === "SUCCESS") {
          // Backend set HttpOnly auth_token cookie; hydrate Redux from /me.
          await fetchMe();

          setStatus("success");
          setMessage("Login successful! Redirecting to dashboard...");

          setTimeout(() => {
            props.setUser(true);
          }, 1500);
        } else {
          setStatus("error");
          setMessage("Login failed");
          setErrorDetails(response.data.message || "Unable to verify magic link.");
        }
      } catch (error) {
        console.error("[MAGIC-LOGIN] Error:", error);
        setStatus("error");
        setMessage("Login failed");
        setErrorDetails(error.response?.data?.error || error.message || "An unexpected error occurred.");
      }
    };

    verifyMagicLink();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRetryLogin = () => {
    // Redirect to normal login page
    window.location.href = "/v2/user/login";
  };

  const renderContent = () => {
    switch (status) {
      case "verifying":
        return (
          <>
            <CircularProgress size={64} sx={{ mb: 3, color: "#6f2981" }} />
            <Typography variant="h5" gutterBottom>
              {message}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Please wait while we verify your credentials...
            </Typography>
          </>
        );
      case "success":
        return (
          <>
            <CheckCircleIcon className={`${classes.statusIcon} ${classes.successIcon}`} />
            <Typography variant="h5" gutterBottom style={{ color: "#4caf50" }}>
              {message}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              You will be redirected automatically.
            </Typography>
          </>
        );
      case "error":
        return (
          <>
            <ErrorIcon className={`${classes.statusIcon} ${classes.errorIcon}`} />
            <Typography variant="h5" gutterBottom style={{ color: "#f44336" }}>
              {message}
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
              {errorDetails}
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={handleRetryLogin}
              sx={{ mt: 2 }}
            >
              Go to Login Page
            </Button>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <Grid container component="main" id="main-body-two" className={classes.root}>
      <CssBaseline />
      <Grid item xs={false} sm={4} md={7} className={classes.image} />
      <Grid item xs={12} sm={8} md={5} component={Paper} elevation={6} square style={{ display: "table" }}>
        <div className={classes.paper}>
          <img
            className="img-responsive center-block"
            style={{ maxWidth: "450px", maxHeight: "70px", marginBottom: "40px" }}
            src={ahGreenDotLogo}
            alt="AssessHub Logo"
          />

          {renderContent()}

          <Box mt={5}>
            <Copyright />
          </Box>
        </div>
      </Grid>
    </Grid>
  );
}
